/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';
import { Constants } from 'app/shared/models/constants.model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class UtilsService {
  private readonly NO_SCROLL = 'no-scroll';
  private readonly NO_SCROLL_MOBILE = 'no-scroll-xs';
  private readonly MODAL_OPEN = 'modal-open';
  private readonly HAS_SYSTEM_NOTIFICATIONS = 'has-system-notification';

  private readonly ALL_CONTENT = {
    ALL_ATHLETES: 'All Athletes',
    ALL_TEAMS: 'All Teams',
  };

  // Regular expression to separate the digit string from the non-digit strings.
  private reParts = /\d+|\D+/g;

  // Regular expression to test if the string has a digit.
  private reDigit = /\d/;

  constructor(private httpClient: HttpClient) {}

  isMobileOrTablet() {
    let check = false;
    ((a) => {
      // tslint:disable-next-line max-line-length
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      ) {
        check = true;
      }
    })(navigator.userAgent || navigator.vendor);
    return check;
  }

  isChromeAndIOS() {
    let check = false;
    if (this.isMobileOrTablet() && navigator.userAgent.includes('CriOS/')) {
      check = true;
    }
    return check;
  }

  viewportWidthSmallerThan(value: number) {
    return window.innerWidth < value;
  }

  hasSmallDesktopResolution() {
    /* consider the scrollbar */
    return document.body.clientWidth < 1100;
  }

  hasSmallResolution() {
    return window.innerWidth < 1024;
  }

  isMobile() {
    return window.innerWidth < 768;
  }

  getIsModalShownStatus() {
    const el = document.querySelector('html');
    return el && el.classList && el.classList.contains(this.MODAL_OPEN);
  }

  showPageModal() {
    const el = document.querySelector('html');
    if (!el || !el.classList) {
      return;
    }
    el.classList.add(this.NO_SCROLL, this.MODAL_OPEN);
  }

  hidePageModal() {
    const el = document.querySelector('html');
    if (!el || !el.classList) {
      return;
    }
    document.querySelector('html').classList.remove(this.NO_SCROLL, this.MODAL_OPEN);
  }

  togglePageModal() {
    const el = document.querySelector('html');
    if (!el || !el.classList) {
      return;
    }
    el.classList.toggle(this.NO_SCROLL);
    el.classList.toggle(this.MODAL_OPEN);
  }

  unsetNoScrollMobile() {
    const doc = document.querySelector('html');
    const el = document.querySelector('body');
    if (!doc || !doc.classList || !el || !el.classList) {
      return;
    }
    doc.classList.remove(this.NO_SCROLL_MOBILE);
    el.classList.remove(this.NO_SCROLL_MOBILE);
  }

  toggleNoScroll() {
    const doc = document.querySelector('html');
    const el = document.querySelector('body');
    if (!doc || !doc.classList || !el || !el.classList) {
      return;
    }
    doc.classList.toggle(this.NO_SCROLL);
    el.classList.toggle(this.NO_SCROLL);
  }

  setNoScroll() {
    const doc = document.querySelector('html');
    const el = document.querySelector('body');
    if (!doc || !doc.classList || !el || !el.classList) {
      return;
    }
    doc.classList.add(this.NO_SCROLL);
    el.classList.add(this.NO_SCROLL);
  }

  unsetNoScroll() {
    const doc = document.querySelector('html');
    const el = document.querySelector('body');
    if (!doc || !doc.classList || !el || !el.classList) {
      return;
    }
    doc.classList.remove(this.NO_SCROLL);
    el.classList.remove(this.NO_SCROLL);
  }

  getSlugForName(name: string) {
    /* workaround for FireFox */
    let noParenthesesName = name;
    if (name) {
      noParenthesesName = noParenthesesName.replace(/\"|\'|\(|\)/g, '');
    }
    return noParenthesesName ? noParenthesesName.toLowerCase().split(' ').join('-') : '';
  }

  static formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return phoneNumber;
    }
    const numbers = phoneNumber.match(/\d+/g);
    let numberNoFormatting = '';
    if (numbers) {
      numberNoFormatting = numbers.join('');

      if (numberNoFormatting.length <= 3) {
        return `${numberNoFormatting.slice(0, 3)}`;
      }

      if (numberNoFormatting.length <= 6) {
        return `${numberNoFormatting.slice(0, 3)}-${numberNoFormatting.slice(3, 6)}`;
      }

      if (numberNoFormatting.length > 6) {
        return `${numberNoFormatting.slice(0, 3)}-${numberNoFormatting.slice(3, 6)}-${numberNoFormatting.slice(6, 10)}`;
      }
    }
    return numberNoFormatting;
  }

  static formatGPA(gpa: string): string {
    if (!gpa) {
      return gpa;
    }
    const numbers = gpa.match(/^[\d]{1}.?([\d]+)?$/);
    const numberNoFormatting = gpa;
    if (numbers) {
      if (numberNoFormatting.length <= 1) {
        return `${numberNoFormatting.slice(0, 1)}`;
      }

      if (numberNoFormatting.length >= 2 && numberNoFormatting[1] === '.') {
        return `${numberNoFormatting.slice(0, 1)}${numberNoFormatting.slice(1, 4)}`;
      } else {
        if (numberNoFormatting.length <= 2) {
          return `${numberNoFormatting.slice(0, 1)}.${numberNoFormatting.slice(1, 2)}`;
        }
        if (numberNoFormatting.length > 2) {
          return `${numberNoFormatting.slice(0, 1)}${numberNoFormatting.slice(1, 4)}`;
        }
      }
    }
    return numberNoFormatting;
  }

  static formatDate(date: string): string {
    if (!date) {
      return date;
    }
    const numbers = date.match(/\d+/g);
    let numberNoFormatting = '';
    if (numbers) {
      numberNoFormatting = numbers.join('');

      if (numberNoFormatting.length <= 2) {
        return `${numberNoFormatting.slice(0, 2)}`;
      }

      if (numberNoFormatting.length <= 4) {
        return `${numberNoFormatting.slice(0, 2)}/${numberNoFormatting.slice(2, 4)}`;
      }

      if (numberNoFormatting.length > 4) {
        return `${numberNoFormatting.slice(0, 2)}/${numberNoFormatting.slice(2, 4)}/${numberNoFormatting.slice(4, 6)}`;
      }
    }
    return numberNoFormatting;
  }

  static formatTime(time: string): string {
    if (!time) {
      return time;
    }
    const numbers = time.match(/\d+/g);
    let numberNoFormatting = '';
    if (numbers && numbers.length) {
      numberNoFormatting = numbers.join('');

      let hour: any = parseInt(numberNoFormatting.slice(0, 2));
      let minutes: any = parseInt(numberNoFormatting.slice(2, 4));

      if (!isNaN(hour)) {
        if (hour > 2 && hour < 10) {
          hour = `0${hour}`;
        } else if (hour > 12) {
          hour = `12`;
        }
      }
      if (!isNaN(minutes)) {
        if (minutes > 5 && minutes < 10) {
          minutes = `0${minutes}`;
        } else if (minutes > 59) {
          minutes = `59`;
        }
      }

      if (numberNoFormatting.length <= 2) {
        return `${hour}`;
      }

      if (numberNoFormatting.length > 2) {
        return `${hour}:${minutes}`;
      }
    }
    return numberNoFormatting;
  }

  getPlayersDropdownPlaceholder(sportName, eventType?) {
    if (this.isAthleteSport(sportName, eventType)) {
      return this.ALL_CONTENT.ALL_ATHLETES;
    }
    return this.ALL_CONTENT.ALL_TEAMS;
  }

  isAthleteSport(sportName, eventType) {
    if (eventType) {
      return (
        eventType !== Constants.EVENT_TYPES.TEAM &&
        (sportName.toLowerCase().includes('baseball') || sportName.toLowerCase().includes('softball'))
      );
    }
    return sportName.toLowerCase().includes('baseball') || sportName.toLowerCase().includes('softball');
  }

  nameSort(a: any, b: any, property: string) {
    if (a[property] === this.ALL_CONTENT.ALL_TEAMS || a[property] === this.ALL_CONTENT.ALL_ATHLETES) {
      return -1;
    }

    if (b[property] === this.ALL_CONTENT.ALL_TEAMS || b[property] === this.ALL_CONTENT.ALL_ATHLETES) {
      return 1;
    }

    // Separates the strings into substrings that have only digits and those
    // that have no digits.
    const aParts = a[property].match(this.reParts);
    const bParts = b[property].match(this.reParts);

    // Used to determine if aPart and bPart are digits.
    let isDigitPart = this.reDigit.test(aParts[0]);

    // If `a` and `b` are strings with substring parts that match...
    if (aParts && bParts && isDigitPart === this.reDigit.test(bParts[0])) {
      // Loop through each substring part to compare the overall strings.
      const len = Math.min(aParts.length, bParts.length);
      for (let i = 0; i < len; i++) {
        let aPart = aParts[i];
        let bPart = bParts[i];

        // If comparing digits, convert them to numbers (assuming base 10).
        if (isDigitPart) {
          aPart = parseInt(aPart, 10);
          bPart = parseInt(bPart, 10);
        }

        // If the substrings aren't equal, return either -1 or 1.
        if (aPart !== bPart) {
          return aPart < bPart ? -1 : 1;
        }

        // Toggle the value of isDigitPart since the parts will alternate.
        isDigitPart = !isDigitPart;
      }
    }

    // Use normal comparison.
    return a[property] > b[property] ? 1 : a[property] < b[property] ? -1 : 0;
  }

  generateRandomString(length: number) {
    return Array(length)
      .fill(0)
      .map((x) => Math.random().toString(36).charAt(2)) // NOSONAR
      .join('');
  }

  log(logType, message) {
    return this.httpClient.post(`${environment.apiHostname}/v1/utils/log`, { logType, message });
  }

  logToServer(logType, user, payload, error?) {
    let logMessage = `[user] ${JSON.stringify(user)},
                      [payload] ${JSON.stringify(payload)}
                      `;
    if (error) {
      logMessage += `[error] ${JSON.stringify(error)}`;
    } else {
      // if no error object, then is success
      logMessage += `[info] SUCCESS`;
    }

    this.log(logType, logMessage).subscribe();
  }

  checkAvailableBitrates(videoUrl) {
    let urlToCheck = videoUrl.split('.mp4')[0];
    urlToCheck = urlToCheck.replace(/(_480p_30fps|_720p_30fps|_720p_60fps)$/, '');
    urlToCheck = urlToCheck.replace(/#/g, '%23');
    /* prepare gcs urls */
    urlToCheck = urlToCheck.replace(Constants.GS_ASSET_HOSTNAME, '');
    /* prepare clips urls */
    urlToCheck = urlToCheck.replace(environment.fastly.main, 'video_assets');
    if (urlToCheck.startsWith('/')) {
      urlToCheck = urlToCheck.replace('/', '');
    }
    return this.httpClient.get(`${environment.apiHostname}/v1/utils/check-file?file=${urlToCheck + '.mp4'}`, {});
  }

  purchaseIncludesHR(purchase) {
    return (
      purchase.type === Constants.PURCHASE_TYPE.HR ||
      purchase.type === Constants.PURCHASE_TYPE.PRO_HR ||
      purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_GFHR ||
      purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_GFHRP ||
      purchase.type === Constants.PURCHASE_TYPE.COMPLETE_GP ||
      purchase.type === Constants.PURCHASE_TYPE.PERFECT_GP
    );
  }

  sortStringByProp(a, b, propName = 'name') {
    if (!a) {
      return 1;
    }
    if (!b) {
      return -1;
    }
    return `${a[propName]}`.localeCompare(b[propName]);
  }
}
